import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { toast } from 'sonner';

import { useResourceCategories, useResourceUnits } from './hooks/useResourceRates';
import { ResourceType } from './types';

// ==========================================
// Reusable UI Components
// ==========================================

const FormSection = ({ title, icon, children }: { title: string, icon: string, children: React.ReactNode }) => (
  <div className="mb-8 last:mb-0 animate-in fade-in slide-in-from-bottom-2 duration-500">
    <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-2">
      <i className={`${icon} text-emerald-600 text-lg`}></i>
      <h3 className="text-sm font-normal tracking-wide text-slate-800 tracking-widest">{title}</h3>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
      {children}
    </div>
  </div>
);

const InputField = ({ label, required, placeholder, type = "text", icon, prefix, suffix, value, onChange }: any) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-normal tracking-wider text-slate-500 tracking-widest flex items-center gap-1">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    <div className="relative group">
      {icon && <i className={`${icon} absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors`}></i>}
      <input 
        type={type} 
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        className={`w-full ${icon ? 'pl-9' : 'px-4'} py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-normal text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all shadow-sm group-hover:border-slate-300`}
        placeholder={placeholder}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-normal text-slate-400 tracking-widest tracking-tighter">{suffix}</span>}
    </div>
  </div>
);

const SelectField = ({ label, required, options, icon, isLoading, value, onChange, parentSelected = true }: { label: string, required?: boolean, options: { label: string, value: string }[], icon?: string, isLoading?: boolean, value?: string, onChange?: (val: string) => void, parentSelected?: boolean }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-normal tracking-wider text-slate-500 tracking-widest">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    <div className="relative group">
      {icon && <i className={`${icon} absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors z-10`}></i>}
      <select 
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        className={`w-full ${icon ? 'pl-9' : 'px-4'} py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-normal text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all shadow-sm appearance-none group-hover:border-slate-300`}
      >
        <option value="">-- Chọn {label} --</option>
        {isLoading ? <option>Đang tải...</option> : options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        {!isLoading && options.length === 0 && parentSelected && <option disabled>Không có dữ liệu phù hợp</option>}
        {!isLoading && options.length === 0 && !parentSelected && <option disabled>Vui lòng chọn cấp 1 trước</option>}
      </select>
      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"></i>
    </div>
  </div>
);

// ==========================================
// Specific Form Components
// ==========================================

const MaterialsForm = ({ formData, handleFieldChange, parentCategories, childCategories, isCatsLoading, units, isUnitsLoading }: any) => (
  <>
    <FormSection title="Thông tin cơ bản" icon="ri-information-line">
      <InputField 
        label="Mã tài nguyên" 
        required 
        placeholder="VD: VL-001" 
        icon="ri-fingerprint-line" 
        value={formData.code} 
        onChange={(val: string) => handleFieldChange('code', val)} 
      />
      <SelectField 
        label="Danh mục cấp 1" 
        icon="ri-folders-line" 
        options={parentCategories} 
        isLoading={isCatsLoading} 
        value={formData.parent_id} 
        onChange={(val: string) => handleFieldChange('parent_id', val)} 
      />
      <SelectField 
        label="Danh mục cấp 2" 
        icon="ri-folder-add-line" 
        options={childCategories} 
        isLoading={isCatsLoading} 
        value={formData.category_id} 
        onChange={(val: string) => handleFieldChange('category_id', val)} 
        parentSelected={!!formData.parent_id} 
      />
      <div className="md:col-span-2">
        <InputField 
          label="Tên chi tiết tài nguyên" 
          required 
          placeholder="Nhập tên vật tư đầy đủ..." 
          value={formData.name} 
          onChange={(val: string) => handleFieldChange('name', val)} 
        />
      </div>
      <SelectField 
        label="Đơn vị tính" 
        icon="ri-ruler-line" 
        options={units?.map((u: any) => ({ label: u, value: u })) || []} 
        isLoading={isUnitsLoading} 
        value={formData.unit} 
        onChange={(val: string) => handleFieldChange('unit', val)} 
      />
      <InputField 
        label="Quy cách đóng gói" 
        placeholder="Bao 25kg, Khối..." 
        icon="ri-archive-line" 
        value={formData.specifications?.packaging} 
        onChange={(val: string) => handleFieldChange('specifications', { ...formData.specifications, packaging: val })} 
      />
    </FormSection>
    <FormSection title="Định giá & Định mức" icon="ri-price-tag-3-line">
      <div className="md:col-span-2 bg-emerald-50/30 p-4 rounded-xl border border-emerald-100/50 mb-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField 
            label="Đơn giá cơ sở" 
            required 
            placeholder="0" 
            suffix="VNĐ" 
            type="number" 
            value={formData.base_price} 
            onChange={(val: string) => handleFieldChange('base_price', val)} 
          />
          <InputField label="Đơn giá thị trường (AI)" placeholder="Tự động cập nhật" icon="ri-robot-2-line" />
        </div>
      </div>
      <InputField 
        label="Tỷ lệ hao hụt dự kiến" 
        placeholder="5" 
        suffix="%" 
        value={formData.waste_rate} 
        onChange={(val: string) => handleFieldChange('waste_rate', val)} 
      />
      <InputField 
        label="Thuế giá trị gia tăng" 
        placeholder="8" 
        suffix="%" 
        value={formData.tax_rate} 
        onChange={(val: string) => handleFieldChange('tax_rate', val)} 
      />
      <div className="md:col-span-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-normal tracking-wider text-slate-500 tracking-widest">Ghi chú kỹ thuật</label>
          <textarea 
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-normal text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all shadow-sm min-h-[100px]" 
            placeholder="Yêu cầu về chất lượng, nguồn gốc..."
            value={formData.note}
            onChange={(e) => handleFieldChange('note', e.target.value)}
          ></textarea>
        </div>
      </div>
    </FormSection>
  </>
);

const PlantsForm = ({ formData, handleFieldChange, parentCategories, childCategories, isCatsLoading }: any) => (
  <>
    <FormSection title="Đặc tính thực vật" icon="ri-leaf-line">
      <InputField 
        label="Mã cây trồng" 
        required 
        placeholder="VD: CX-001" 
        value={formData.code} 
        onChange={(val: string) => handleFieldChange('code', val)} 
      />
      <InputField 
        label="Tên phổ thông" 
        required 
        placeholder="VD: Bàng Đài Loan" 
        value={formData.name} 
        onChange={(val: string) => handleFieldChange('name', val)} 
      />
      <InputField 
        label="Tên khoa học" 
        placeholder="Terminalia mantaly" 
        value={formData.specifications?.scientific_name} 
        onChange={(val: string) => handleFieldChange('specifications', { ...formData.specifications, scientific_name: val })} 
      />
      <SelectField 
        label="Nhóm cây cấp 1" 
        options={parentCategories} 
        isLoading={isCatsLoading} 
        value={formData.parent_id} 
        onChange={(val: string) => handleFieldChange('parent_id', val)} 
      />
      <SelectField 
        label="Nhóm cây cấp 2" 
        options={childCategories} 
        isLoading={isCatsLoading} 
        value={formData.category_id} 
        onChange={(val: string) => handleFieldChange('category_id', val)} 
        parentSelected={!!formData.parent_id} 
      />
    </FormSection>
    <FormSection title="Thông số quy cách" icon="ri-ruler-2-line">
      <InputField label="Chiều cao (H)" placeholder="VD: 2.5 - 3.0" suffix="Mét" />
      <InputField label="Đường kính thân (D)" placeholder="VD: 10 - 12" suffix="Cm" />
      <InputField label="Đường kính tán (R)" placeholder="VD: 1.5 - 2.0" suffix="Mét" />
      <SelectField label="Tình trạng bầu" options={[{ label: "Bầu đất", value: "Bầu đất" }, { label: "Bầu sơ dừa", value: "Bầu sơ dừa" }, { label: "Dâm ủ tại vườn", value: "Dâm ủ tại vườn" }, { label: "Đánh thẳng", value: "Đánh thẳng" }]} />
    </FormSection>
    <FormSection title="Chi phí thành phần" icon="ri-wallet-3-line">
      <InputField label="Giá gốc tại vườn" required suffix="VNĐ" type="number" value={formData.base_price} onChange={(val: string) => handleFieldChange('base_price', val)} />
      <InputField label="Phí bốc xếp & Vận chuyển" suffix="VNĐ" type="number" />
      <InputField label="Tỷ lệ hao hụt & Bảo hành" placeholder="10" suffix="%" />
    </FormSection>
  </>
);

const LaborForm = ({ formData, handleFieldChange, parentCategories, childCategories, isCatsLoading }: any) => (
  <>
    <FormSection title="Thông tin nhân sự" icon="ri-user-settings-line">
      <InputField label="Mã nhân công" required placeholder="VD: NC-001" value={formData.code} onChange={(val: string) => handleFieldChange('code', val)} />
      <SelectField label="Nhóm cấp 1" options={parentCategories} isLoading={isCatsLoading} value={formData.parent_id} onChange={(val: string) => handleFieldChange('parent_id', val)} />
      <SelectField label="Nhóm cấp 2" options={childCategories} isLoading={isCatsLoading} value={formData.category_id} onChange={(val: string) => handleFieldChange('category_id', val)} parentSelected={!!formData.parent_id} />
      <div className="md:col-span-2">
        <InputField label="Chức danh / Vị trí" required placeholder="VD: Thợ mộc bậc 3/7" value={formData.name} onChange={(val: string) => handleFieldChange('name', val)} />
      </div>
    </FormSection>
    <FormSection title="Định giá định mức" icon="ri-money-dollar-circle-line">
      <InputField label="Đơn giá ngày công" required type="number" suffix="VNĐ" value={formData.base_price} onChange={(val: string) => handleFieldChange('base_price', val)} />
      <InputField label="Đơn giá tăng ca (OT)" type="number" suffix="VNĐ" />
    </FormSection>
  </>
);

const EquipmentForm = ({ formData, handleFieldChange, parentCategories, childCategories, isCatsLoading }: any) => (
  <>
    <FormSection title="Thông tin thiết bị" icon="ri-truck-line">
      <InputField label="Mã thiết bị" required placeholder="VD: TB-001" value={formData.code} onChange={(val: string) => handleFieldChange('code', val)} />
      <SelectField label="Nhóm cấp 1" options={parentCategories} isLoading={isCatsLoading} value={formData.parent_id} onChange={(val: string) => handleFieldChange('parent_id', val)} />
      <SelectField label="Nhóm cấp 2" options={childCategories} isLoading={isCatsLoading} value={formData.category_id} onChange={(val: string) => handleFieldChange('category_id', val)} parentSelected={!!formData.parent_id} />
      <div className="md:col-span-2">
        <InputField label="Tên máy móc / Thiết bị" required placeholder="VD: Máy xúc Komatsu 0.3m3" value={formData.name} onChange={(val: string) => handleFieldChange('name', val)} />
      </div>
    </FormSection>
    <FormSection title="Chi phí vận hành" icon="ri-gas-station-line">
      <InputField label="Đơn giá thuê (Ca/Ngày)" type="number" suffix="VNĐ" value={formData.base_price} onChange={(val: string) => handleFieldChange('base_price', val)} />
      <InputField label="Nhiên liệu tiêu hao dự kiến" type="number" suffix="Lít/H" />
    </FormSection>
  </>
);

// ==========================================
// Main Page Component
// ==========================================

export function ResourceFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type') || 'materials';
  const mode = searchParams.get('mode') || 'new';

  const typeMap: Record<string, string | undefined> = {
    'materials': 'material',
    'plants': 'material',
    'labor': 'labor',
    'equipment': 'equipment',
    'fuel': 'material',
    'agro': 'material',
    'suppliers': undefined
  };

  const prefixMap: Record<string, string> = {
    'materials': 'VL-',
    'plants': 'CX-',
    'labor': 'NC-',
    'equipment': 'TB-',
    'fuel': 'NL-',
    'agro': 'PB-',
    'suppliers': 'NCC-'
  };

  const { data: categories, isLoading: isCatsLoading } = useResourceCategories(typeMap[typeParam]);
  const { data: units, isLoading: isUnitsLoading } = useResourceUnits();

  // Unified Form State
  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState<any>({
    code: '',
    name: '',
    parent_id: '',
    category_id: '',
    unit: '',
    specifications: {},
    base_price: '',
    tax_rate: '8',
    waste_rate: '5',
    note: ''
  });

  // Handle field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  // Reset and set default code when switching tabs
  React.useEffect(() => {
    setFormData({
      code: prefixMap[typeParam] || '',
      name: '',
      parent_id: '',
      category_id: '',
      unit: '',
      specifications: {},
      base_price: '',
      tax_rate: '8',
      waste_rate: '5',
      note: ''
    });
  }, [typeParam]);

  const parentCategories = React.useMemo(() => {
    if (!categories) return [];
    return categories.filter(cat => !cat.parent_id).map(cat => ({ value: cat.id, label: cat.name }));
  }, [categories]);

  const childCategories = React.useMemo(() => {
    if (!categories || !formData.parent_id) return [];
    return categories.filter(cat => cat.parent_id === formData.parent_id).map(cat => ({ value: cat.id, label: cat.name }));
  }, [categories, formData.parent_id]);

  const getTitle = () => {
    const action = mode === 'edit' ? 'Cập nhật' : 'Thêm mới';
    switch (typeParam) {
      case 'materials': return `${action} Vật liệu nền & Cảnh quan`;
      case 'plants': return `${action} Cây xanh & Thảm cỏ`;
      case 'labor': return `${action} Định mức Nhân công`;
      case 'equipment': return `${action} Máy móc & Thiết bị`;
      case 'fuel': return `${action} Nhiên liệu & Năng lượng`;
      case 'agro': return `${action} Phân bón & Thuốc BVTV`;
      case 'suppliers': return `${action} Thông tin Nhà cung cấp`;
      default: return `${action} Dữ liệu Master Data`;
    }
  };

  const commonProps = {
    formData,
    handleFieldChange,
    parentCategories,
    childCategories,
    isCatsLoading,
    units,
    isUnitsLoading
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code) {
      toast.error('Vui lòng nhập đầy đủ Mã và Tên tài nguyên');
      return;
    }

    setIsSaving(true);
    try {
      // Simulate save for now
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Lưu dữ liệu tài nguyên thành công!', {
        description: `Tài nguyên ${formData.code} đã được cập nhật vào hệ thống.`,
      });
      setTimeout(() => navigate('/estimates/rates'), 2000);
    } catch (error) {
      toast.error('Có lỗi xảy ra khi lưu dữ liệu');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderFormContent = () => {
    switch (typeParam) {
      case 'materials': 
      case 'fuel':
      case 'agro':
        return <MaterialsForm {...commonProps} />;
      case 'plants': return <PlantsForm {...commonProps} />;
      case 'labor': return <LaborForm {...commonProps} />;
      case 'equipment': return <EquipmentForm {...commonProps} />;
      default: return <MaterialsForm {...commonProps} />;
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-64 bg-emerald-500/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
      
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-50 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/estimates/rates')} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 transition-colors">
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <div>
            <h1 className="text-base font-normal tracking-tight text-slate-900">{getTitle()}</h1>
            <p className="text-[11px] text-slate-400 font-normal tracking-widest tracking-widest">
              Master Data <span className="mx-1 opacity-50">/</span> Định giá nguồn lực
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 flex flex-col gap-6">
            <Card className="bg-white border-slate-200 shadow-xl shadow-slate-200/40 p-8 rounded-2xl">
              <div className="flex justify-between items-start mb-10">
                <div>
                  <Badge variant="info" className="mb-2">{mode === 'edit' ? 'Chế độ chỉnh sửa' : 'Tạo mới hồ sơ'}</Badge>
                  <h2 className="text-xl font-normal tracking-tight text-slate-900">Chi tiết cấu hình</h2>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                  <i className="ri-draft-line text-2xl"></i>
                </div>
              </div>
              {renderFormContent()}
            </Card>

            {/* Final Action Block */}
            <div className="bg-white border border-slate-200 p-4 rounded-2xl flex items-center justify-between shadow-sm shadow-slate-200/40">
              <span className="text-[11px] text-slate-400 italic flex items-center gap-1.5 ml-2">
                <i className="ri-time-line text-slate-300"></i> Bản nháp tự động lưu: 2 phút trước
              </span>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => navigate('/estimates/rates')} 
                  className="h-10 px-6 bg-slate-50 border border-slate-200 rounded-xl text-xs font-normal tracking-wide text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all"
                >
                  Hủy bỏ
                </button>
                <button 
                  onClick={handleSave} 
                  disabled={isSaving}
                  className={`h-10 px-8 bg-emerald-700 rounded-xl text-xs font-normal tracking-wide text-white hover:bg-emerald-800 transition-all flex items-center gap-2 shadow-lg shadow-emerald-700/20 ${isSaving ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSaving ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-save-3-line"></i>
                  )}
                  {isSaving ? 'Đang lưu dữ liệu...' : 'Lưu & Hoàn tất'}
                </button>
              </div>
            </div>
            
            <div className="h-10"></div> {/* Spacer for bottom padding */}
          </div>

          <div className="lg:col-span-4 flex flex-col gap-6">
            <Card className="p-6 border-emerald-100 shadow-sm bg-emerald-50/40 rounded-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity">
                <i className="ri-robot-2-line text-[100px] -rotate-12"></i>
              </div>
              <h4 className="text-xs font-normal tracking-widest text-emerald-800 tracking-widest mb-4 flex items-center gap-2">
                <i className="ri-robot-2-line"></i> AI Form Assistance
              </h4>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-check-line text-emerald-600 text-xs"></i>
                  </div>
                  <p className="text-xs text-emerald-900/70 font-normal leading-relaxed">
                    Đã phát hiện mã tài nguyên tương tự <span className="font-medium text-emerald-800">VL-001a</span>. Bạn có muốn sử dụng quy tắc đặt mã tự động không?
                  </p>
                </li>
                <li className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-amber-600 text-xs"></i>
                  </div>
                  <p className="text-xs text-amber-900/70 font-normal leading-relaxed">
                    Dựa trên nhóm danh mục, đơn giá trung bình thị trường dao động từ <span className="font-medium text-amber-800">120k - 150k</span>.
                  </p>
                </li>
              </ul>
              <button className="mt-6 w-full py-2 bg-emerald-600 text-white rounded-xl text-xs font-normal tracking-wide shadow-md shadow-emerald-700/10 hover:bg-emerald-700 transition-all">
                Áp dụng gợi ý AI
              </button>
            </Card>

            <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
              <h4 className="text-xs font-normal tracking-widest text-slate-400 tracking-widest mb-4">Hướng dẫn nhập liệu</h4>
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs font-normal text-slate-800 mb-1">Mã tài nguyên</p>
                  <p className="text-[11px] text-slate-500 font-normal leading-relaxed">Nên viết hoa toàn bộ và không có dấu cách. VD: CAT_SAN_LAP.</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs font-normal text-slate-800 mb-1">Quy cách kỹ thuật</p>
                  <p className="text-[11px] text-slate-500 font-normal leading-relaxed">Thông tin này sẽ xuất hiện trực tiếp trên bản báo giá gửi khách hàng.</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
